import { ZoomTransform } from "d3";
import { computed, type ComputedRef, type Ref } from "vue";
import type { AxisRange, AxisScale, AxisSide, CartesianChartOptions } from "..";
import type CartesianChartVue from "../components/CartesianChart.vue";

function rangeLength({ min, max }: AxisRange) {
	return max.valueOf() - min.valueOf();
}

type ZoomTransformCreation = {
	side: AxisSide;
	transform?: ZoomTransform;
	scale: AxisScale;
	range: AxisRange;
	zoom: AxisRange;
};

export function createZoomTransform({
	side,
	transform,
	scale,
	range,
	zoom,
}: ZoomTransformCreation) {
	const k = rangeLength(range) / rangeLength(zoom);
	const offset = (scale(range.min) - scale(zoom.min)) * k;

	const type = side === "bottom" || side === "top" ? "x" : "y";

	const x = type === "x" ? offset : transform?.x || 0;
	const y = type === "y" ? offset : transform?.y || 0;

	return new ZoomTransform(k, x, y);
}

export function useManualZoom(
	chart: Ref<InstanceType<typeof CartesianChartVue> | undefined>,
	options: ComputedRef<CartesianChartOptions>
) {
	function roundFloat(n: number) {
		return +n.toFixed(2);
	}
	function isSameTransform(a: ZoomTransform, b: ZoomTransform) {
		return (
			roundFloat(a.k) === roundFloat(b.k) &&
			roundFloat(a.x) === roundFloat(b.x) &&
			roundFloat(a.y) === roundFloat(b.y)
		);
	}

	const transform = computed({
		get: () => chart.value?.svgContainer?.transform,
		set: (transform) => {
			if (!chart.value?.svgContainer || !transform) return;
			chart.value.svgContainer.transform = transform;
		},
	});

	async function setZoom(side: AxisSide, zoom: AxisRange) {
		if (!chart.value?.svgContainer) return;

		const scale = chart.value.originalScale[side];
		if (!scale) return;

		const range = options.value.axes.find((a) => a.side === side)?.range;
		if (!range) return;

		const newTransform = createZoomTransform({
			side,
			range,
			zoom,
			scale,
			transform: transform.value,
		});

		if (transform.value && isSameTransform(transform.value, newTransform)) return;

		chart.value.svgContainer.setTransform(newTransform);
	}

	return { setZoom };
}
